import {isArray, isEmpty} from 'lodash';
import {
  AgeRange,
  AudienceFilter,
  BasePlatformFilter,
  Gender,
  InstagramFilter,
  IntRange,
  LocationValue,
  MultiPlatformFilter,
  PlatformFilters,
  ProfileFilter,
  Sorting,
  TiktokFilter,
  VettingFilter,
  CampaignHistoryFilter,
  WeightedFilter,
  YoutubeFilter,
  SearchState,
} from '@/types/models/creator-crm/creatorCrm';
import {
  AudienceAgeSelection,
  BaseFilterOption,
  DynamicPlatformFilters,
  Filter,
  FiltersGroup,
  MultiSelection,
  MultiUserInput,
  RangeSelection,
  SingleSelection,
  SingleUserInput,
  WeightedFilterOption,
  WeightedMultiSelection,
  WeightedSingleSelection,
} from '@/types/models/creator-crm/filter';
import {
  BasePlatformFilterId,
  CreatorDetailsFilterId,
  FilterIds,
  FiltersGroupId,
  PlatformFilterId,
  PlatformFilterIds,
  PlatformId,
  TiktokFilterId,
  YoutubeFilterId,
} from '@/types/models/creator-crm/filterId';
import SortField from '@/types/models/creator-crm/sortField';
import {
  SortDirection as StateSortVariant,
  SortingState,
} from '@/types/models/creator-crm/sorting';

export function mapToSorting(
  sorting: SortingState | undefined
): Sorting | undefined {
  if (!sorting || !validateSorting(sorting)) {
    return undefined;
  }

  return {
    field: sorting.field,
    ascending: mapToIsAscending(sorting.direction),
  };
}

export function mapToMultiplatformFilter(
  filterGroups: Record<FiltersGroupId, FiltersGroup<FilterIds>>,
  searchState?: SearchState
): MultiPlatformFilter {
  const {creatorDetails, platforms} = filterGroups;
  const creatorDetailsFiltersGroup =
    creatorDetails as FiltersGroup<CreatorDetailsFilterId>;
  const platformsFiltersGroup = platforms as FiltersGroup<PlatformFilterId>;
  const multiPlatformFilter: MultiPlatformFilter = {
    profile: mapToProfileFilter(creatorDetailsFiltersGroup),
    audience: mapToAudienceFilter(platformsFiltersGroup),
    platforms: mapToPlatformFilters(platformsFiltersGroup),
    vetting: mapToVettingFilter(creatorDetailsFiltersGroup),
    campaignHistory: mapToCampaignHistoryFilter(creatorDetailsFiltersGroup),
  };

  if (searchState && searchState.chips.length > 0) {
    const {type, chips} = searchState;
    switch (type) {
      case 'bio':
        if (!multiPlatformFilter.profile) {
          multiPlatformFilter.profile = {};
        }
        multiPlatformFilter.profile.bio = chips;
        break;
      case 'handle': {
        const [firstChip] = chips;
        multiPlatformFilter.compoundName = firstChip;
        break;
      }
      case 'content':
        if (!multiPlatformFilter.profile) {
          multiPlatformFilter.profile = {};
        }
        multiPlatformFilter.profile.captions = chips;
        break;
      case 'all':
        multiPlatformFilter.compound = chips;
        break;
      default:
        break;
    }
  }

  return multiPlatformFilter;
}

type AppliedFilters = Partial<Record<FilterIds, unknown>>;
type AppliedFiltersGroups = Partial<Record<FiltersGroupId, AppliedFilters>>;

export function mapToAppliedFilters(
  filterGroups: Record<FiltersGroupId, FiltersGroup<FilterIds>>
): AppliedFiltersGroups | undefined {
  const appliedFiltersGroups: AppliedFiltersGroups = {};

  function getSelected(filter: Filter): unknown | undefined {
    switch (filter.type) {
      case 'singleSelection': {
        const {selected} = filter as SingleSelection<BaseFilterOption>;
        if (filter.asyncInitializationKey) {
          return selected;
        }
        return selected?.value ? selected.id : undefined;
      }
      case 'weightedSingleSelection': {
        const {selected} = filter as SingleSelection<WeightedFilterOption>;
        if (filter.asyncInitializationKey) {
          return selected;
        }
        return selected?.value ? [selected.id, selected.weight] : undefined;
      }
      case 'multiSelection': {
        const {selected} = filter as MultiSelection<BaseFilterOption>;
        if (filter.asyncInitializationKey && selected.length) {
          return selected;
        }
        if (selected.length) {
          return selected.map((option) => option.id);
        }
        return undefined;
      }
      case 'weightedMultiSelection': {
        const {selected} = filter as MultiSelection<WeightedFilterOption>;
        if (filter.asyncInitializationKey && selected.length) {
          return selected;
        }
        if (selected.length) {
          return selected.map((option) => [option.value, option.weight]);
        }
        return undefined;
      }
      case 'multiSelectionAsync': {
        if ((filter as MultiSelection<WeightedFilterOption>).selected.length) {
          return (filter as MultiSelection<WeightedFilterOption>).selected;
        }
        return undefined;
      }
      case 'audienceAge':
        return (
          (filter as AudienceAgeSelection).ldaSelected ||
          ((filter as AudienceAgeSelection).selected.length
            ? (filter as AudienceAgeSelection).selected.map((option) => [
                option.id,
                option.weight,
              ])
            : undefined)
        );
      case 'rangeSelection':
        return (filter as RangeSelection).selected;
      case 'multiUserInput':
        return (filter as MultiUserInput).inputs.length
          ? (filter as MultiUserInput).inputs
          : undefined;
      case 'singleUserInput':
        return (filter as SingleUserInput).input
          ? (filter as SingleUserInput).input
          : undefined;
      case 'dynamicPlatformFilters': {
        const dpf = filter as DynamicPlatformFilters;
        const selectedPlatformFilter = dpf.platformsFilters[dpf.platformId];
        const appliedPlatformFilters: Partial<Record<string, unknown>> = {};
        Object.entries<Filter>(selectedPlatformFilter).forEach(
          ([platformFilterId, platformFilter]) => {
            const selected = getSelected(platformFilter);
            if (selected) {
              appliedPlatformFilters[platformFilterId] = selected;
            }
          }
        );
        const ensuredAppliedPlatformFilters = ensureValuesExist(
          appliedPlatformFilters
        );
        return ensuredAppliedPlatformFilters
          ? [dpf.platformId, ensuredAppliedPlatformFilters]
          : undefined;
      }
      default:
        return undefined;
    }
  }

  Object.entries(filterGroups).forEach(([groupId, group]) => {
    const appliedFilters: AppliedFilters = {};
    Object.entries(group.filters).forEach(([filterId, filter]) => {
      const selected = getSelected(filter);
      if (selected) {
        appliedFilters[filterId as FilterIds] = selected;
      }
    });
    const ensuredAppliedFilters = ensureValuesExist(appliedFilters);
    if (ensuredAppliedFilters) {
      appliedFiltersGroups[groupId as FiltersGroupId] = ensuredAppliedFilters;
    }
  });
  return ensureValuesExist(appliedFiltersGroups);
}

function mapToBasePlatformFilter(
  platformFilters: Partial<Record<PlatformFilterIds, Filter>>
): BasePlatformFilter {
  const basePlatformFilter: BasePlatformFilter = {};
  const stateBasePlatformFilters = platformFilters as Record<
    BasePlatformFilterId,
    Filter
  >;
  if (stateBasePlatformFilters.followers.type === 'rangeSelection') {
    const followers = mapToType<IntRange>(stateBasePlatformFilters.followers);
    if (!isArray(followers)) {
      basePlatformFilter.followers = followers;
    }
  }
  if (stateBasePlatformFilters.engagementRate.type === 'singleSelection') {
    const engagementRate = mapToType<number>(
      stateBasePlatformFilters.engagementRate
    );
    if (!isArray(engagementRate)) {
      basePlatformFilter.engagementRate = engagementRate;
    }
  }
  return basePlatformFilter;
}

function mapToInstagramFilter(
  platformFilters: Partial<Record<PlatformFilterIds, Filter>>
): InstagramFilter {
  return mapToBasePlatformFilter(platformFilters);
}

function mapToTiktokFilter(
  platformFilters: Partial<Record<PlatformFilterIds, Filter>>
): TiktokFilter {
  const basePlatformFilter: BasePlatformFilter | undefined =
    mapToBasePlatformFilter(platformFilters);
  const tiktokFilter: TiktokFilter = {
    ...basePlatformFilter,
  };
  const stateTiktokFilters = platformFilters as Record<TiktokFilterId, Filter>;
  if (stateTiktokFilters.averageViews.type === 'rangeSelection') {
    const viewsCount = mapToType<IntRange>(stateTiktokFilters.averageViews);
    if (!isArray(viewsCount)) {
      tiktokFilter.viewsCount = viewsCount;
    }
  }
  return tiktokFilter;
}

function mapToYoutubeFilter(
  platformFilters: Partial<Record<PlatformFilterIds, Filter>>
): YoutubeFilter {
  const basePlatformFilter: BasePlatformFilter | undefined =
    mapToBasePlatformFilter(platformFilters);
  const youTubeFilter: YoutubeFilter = {
    ...basePlatformFilter,
  };
  const stateYoutubeFilters = platformFilters as Record<
    YoutubeFilterId,
    Filter
  >;
  if (stateYoutubeFilters.averageViews.type === 'rangeSelection') {
    const viewsCount = mapToType<IntRange>(stateYoutubeFilters.averageViews);
    if (!isArray(viewsCount)) {
      youTubeFilter.viewsCount = viewsCount;
    }
  }
  return youTubeFilter;
}

export function mapToPlatformFilters(
  platformFiltersGroup: FiltersGroup<PlatformFilterId>
): PlatformFilters | undefined {
  const dynamicPlatformFilters = platformFiltersGroup.filters
    .platformMetrics as DynamicPlatformFilters;
  const {platformId} = dynamicPlatformFilters;
  const statePlatformFilters =
    dynamicPlatformFilters.platformsFilters[platformId];
  const resultPlatformFilters: PlatformFilters = {};

  switch (platformId) {
    case 'instagram': {
      resultPlatformFilters[platformId] =
        mapToInstagramFilter(statePlatformFilters);
      break;
    }
    case 'tiktok': {
      resultPlatformFilters[platformId] =
        mapToTiktokFilter(statePlatformFilters);
      break;
    }
    case 'youtube': {
      resultPlatformFilters[platformId] =
        mapToYoutubeFilter(statePlatformFilters);
      break;
    }
    case 'facebook':
    case 'twitter':
    case 'pinterest': {
      resultPlatformFilters[platformId] =
        mapToBasePlatformFilter(statePlatformFilters);
      break;
    }
    case 'all': {
      const basePlatformFilter = ensureValuesExist(
        mapToBasePlatformFilter(statePlatformFilters)
      );
      if (basePlatformFilter) {
        [
          'instagram',
          'tiktok',
          'youtube',
          'facebook',
          'twitter',
          'pinterest',
        ].forEach((id) => {
          resultPlatformFilters[id as PlatformId] = basePlatformFilter;
        });
      }
      break;
    }
    default:
      throw new Error(`Unknown platformId: ${platformId}`);
  }

  return ensureValuesExist(resultPlatformFilters);
}

function mapToWeightedFilter<T>(
  filter: WeightedSingleSelection<WeightedFilterOption>
): WeightedFilter<T> | undefined {
  if (filter.selected?.value) {
    return {
      value: filter.selected.value as T,
      weight: filter.selected.weight,
    };
  }
  return undefined;
}

function mapToWeightedFilters<T>(
  filter: WeightedMultiSelection<WeightedFilterOption> | AudienceAgeSelection
): WeightedFilter<T>[] | undefined {
  if (!isEmpty(filter.selected)) {
    return filter.selected.map((option) => ({
      value: option.value as T,
      weight: option.weight,
    }));
  }
  return undefined;
}

export function mapToProfileFilter(
  profileFiltersGroup: FiltersGroup<CreatorDetailsFilterId>
): ProfileFilter | undefined {
  const profileFilter: ProfileFilter = {};
  const stateProfileFilters = profileFiltersGroup.filters as Record<
    CreatorDetailsFilterId,
    Filter
  >;
  if (stateProfileFilters.age.type === 'rangeSelection') {
    const selectedRange = (stateProfileFilters.age as RangeSelection).selected;
    if (selectedRange) {
      const age = {
        fromAge: selectedRange.min,
        toAge: selectedRange.max,
      };
      profileFilter.age = age as AgeRange;
    }
  }
  if (stateProfileFilters.gender.type === 'singleSelection') {
    const gender = mapToType<Gender>(stateProfileFilters.gender);
    if (!isArray(gender)) {
      profileFilter.gender = gender;
    }
  }
  if (stateProfileFilters.category.type === 'multiSelection') {
    const categories = mapToType<string>(stateProfileFilters.category);
    if (isArray(categories)) {
      profileFilter.categories = categories;
    }
  }
  if (stateProfileFilters.location.type === 'multiSelectionAsync') {
    const locations = mapToType<LocationValue>(stateProfileFilters.location);
    if (isArray(locations)) {
      profileFilter.locations = locations;
    }
  }
  return ensureValuesExist(profileFilter);
}

export function mapToVettingFilter(
  creatorDetailsFiltersGroup: FiltersGroup<CreatorDetailsFilterId>
): VettingFilter | undefined {
  const stateFilters = creatorDetailsFiltersGroup.filters as Record<
    CreatorDetailsFilterId,
    Filter
  >;
  if (stateFilters.vettingDecision?.type === 'multiSelection') {
    const filter =
      stateFilters.vettingDecision as MultiSelection<BaseFilterOption>;
    if (
      filter.selected.length > 0 &&
      filter.selected.length < filter.options.length
    ) {
      const decisions = mapToType<string>(filter);
      if (isArray(decisions)) {
        return {decisions};
      }
    }
  }
  return undefined;
}

export function mapToCampaignHistoryFilter(
  creatorDetailsFiltersGroup: FiltersGroup<CreatorDetailsFilterId>
): CampaignHistoryFilter | undefined {
  const stateFilters = creatorDetailsFiltersGroup.filters as Record<
    CreatorDetailsFilterId,
    Filter
  >;
  if (stateFilters.campaignHistory?.type === 'singleSelection') {
    const filter =
      stateFilters.campaignHistory as SingleSelection<BaseFilterOption>;
    if (filter.selected?.value) {
      return {scope: filter.selected.value as string};
    }
  }
  return undefined;
}

export function mapToAudienceFilter(
  platformFiltersGroup: FiltersGroup<PlatformFilterId>
): AudienceFilter | undefined {
  const audienceFilter: AudienceFilter = {};
  const statePlatformFilters = platformFiltersGroup.filters as Record<
    PlatformFilterId,
    Filter
  >;

  if (
    !statePlatformFilters.location.isIrrelevant &&
    statePlatformFilters.location.type === 'multiSelectionAsync'
  ) {
    const locations = mapToType<LocationValue>(statePlatformFilters.location);
    if (isArray(locations)) {
      audienceFilter.locations = locations;
    }
  }
  if (
    !statePlatformFilters.gender.isIrrelevant &&
    statePlatformFilters.gender.type === 'weightedSingleSelection'
  ) {
    audienceFilter.gender = mapToWeightedFilter<Omit<Gender, 'other'>>(
      statePlatformFilters.gender as WeightedSingleSelection<WeightedFilterOption>
    );
  }
  if (
    !statePlatformFilters.age.isIrrelevant &&
    statePlatformFilters.age.type === 'audienceAge'
  ) {
    const audienceAgeFilter = statePlatformFilters.age as AudienceAgeSelection;
    if (audienceAgeFilter.ldaSelected) {
      audienceFilter.isLdaCompliant = audienceAgeFilter.ldaSelected;
    } else {
      audienceFilter.ages = mapToWeightedFilters<AgeRange>(audienceAgeFilter);
    }
  }
  return ensureValuesExist(audienceFilter);
}

function validateSorting(sorting: SortingState): boolean {
  return (
    Object.values(SortField).includes(sorting.field) &&
    ['ASC', 'DESC'].includes(sorting.direction)
  );
}

function mapToIsAscending(sortVariant: StateSortVariant): boolean {
  return sortVariant === 'ASC';
}

function ensureValuesExist<T extends object>(object: T): T | undefined {
  if (Object.values(object).some((value) => value !== undefined)) {
    return object;
  }
  return undefined;
}

function mapToType<T>(filter: Filter): T | T[] | undefined {
  switch (filter.type) {
    case 'singleSelection': {
      const singleSelection = filter as SingleSelection<BaseFilterOption>;
      if (singleSelection.selected && singleSelection.selected.value) {
        return singleSelection.selected.value as T;
      }
      return undefined;
    }
    case 'multiSelectionAsync':
    case 'multiSelection': {
      const multiSelection = filter as MultiSelection<BaseFilterOption>;
      if (!isEmpty(multiSelection.selected)) {
        return multiSelection.selected.map((option) => option.value as T);
      }
      return undefined;
    }
    case 'multiUserInput': {
      const multiUserInput = filter as MultiUserInput;
      if (!isEmpty(multiUserInput.inputs)) {
        return multiUserInput.inputs as T[];
      }
      return undefined;
    }
    case 'singleUserInput': {
      const singleUserInput = filter as SingleUserInput;
      if (singleUserInput.input) {
        return singleUserInput.input as T;
      }
      return undefined;
    }
    case 'rangeSelection': {
      const rangeSelection = filter as RangeSelection;
      if (rangeSelection.selected) {
        return rangeSelection.selected as T;
      }
      return undefined;
    }
    default:
      return undefined;
  }
}
